/**
 * Copyright 2026 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import type { Order } from '@/types';
import type { BuyAgainPurchase } from '@/features/orderHistory';

export const transformOrdersToPurchases = (orders: Order[]): BuyAgainPurchase[] => {
  const itemsWithOrderContext = orders.flatMap((order) =>
    order.shipmentInfoList.flatMap((shipmentGroup) =>
      shipmentGroup.lineItemList
        .filter((item) => Number(item.itemQuantity) - Number(item.itemQuantityReturned) > 0)
        .map((item) => ({
          ...item.productInfo,
          transactionDate: order.orderSubmittedDate,
          orderStatus: order.orderStatusDescription,
          orderType: order.orderType,
          vehicleDisplayName: item.vehicleDisplayName,
          vehicleFit: item.vehicleFit,
          vehicleFitInfo: item.vehicleFitInfo,
          quantity: item.itemQuantity,
          returnedQuantity: item.itemQuantityReturned,
        })),
    ),
  );

  const deduplicatedItems = itemsWithOrderContext.filter(
    (item, index, itemsArray) =>
      index ===
      itemsArray.findIndex((addedItem) => addedItem.skuId === item.skuId && !!addedItem.detailUrl),
  );

  return deduplicatedItems.map((productInfo) => ({
    skuId: productInfo.skuId,
    imageUrl: productInfo.imageUrl,
    skuDisplayName: productInfo.skuDisplayName,
    detailUrl: productInfo.detailUrl,
    partTerminologyId: productInfo.partTerminologyId || '',
    partGroupId: productInfo.partGroupId || '',
    shelfPageUrl: productInfo.shelfPageUrl,
    brand: productInfo.brand,
    purchasedVehicle: {
      vehicleDisplayName: productInfo.vehicleDisplayName,
      vehicleFit: productInfo.vehicleFit,
      vehicleFitInfo: productInfo.vehicleFitInfo,
    },
    transactionDate: productInfo.transactionDate,
    orderStatus: productInfo.orderStatus,
    orderType: productInfo.orderType,
    quantity: productInfo.quantity,
    returnedQuantity: productInfo.returnedQuantity ?? '0',
    shipmentStatus: 'DELIVERED',
    vehicleSpecific: false,
  }));
};
